
import Vue, { PropType } from "vue";
import { format } from "date-fns";
import { Notification, NotificationFromNolaNovel, NotificationDataType } from "@/lib/models";
import { createUrlWithUtmParams } from "@/lib/utils/url";
import { NolaItemId, NolaPageName } from "@/lib/utils/analytics";

type NotificationType = "agent" | "nolaNovel";

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    notifications: {
      type: Array as PropType<Notification[] | NotificationFromNolaNovel[]>,
      required: true,
    },
    notificationType: {
      type: String as PropType<NotificationType>,
      required: true,
    },
    isLoadComplete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      initialUnreadIds: new Set<string>(),
    };
  },
  watch: {
    notifications: {
      immediate: true,
      handler(newNotifications: Array<Notification | NotificationFromNolaNovel>) {
        // 新しく追加された未読通知のIDを記録（既に記録済みのものは上書きしない）
        newNotifications.forEach((notification) => {
          const key = this.getNotificationKey(notification);
          // まだ記録されていない通知で、未読の場合のみ追加
          if (!this.initialUnreadIds.has(key) && !notification.isRead) {
            this.initialUnreadIds.add(key);
          }
        });
      },
    },
  },
  methods: {
    getNotificationKey(notification: Notification | NotificationFromNolaNovel): string {
      if (this.notificationType === "agent") {
        return (notification as Notification).notificationId;
      }
      return (notification as NotificationFromNolaNovel).typeTargetIdSubTargetId;
    },
    isRead(notification: Notification | NotificationFromNolaNovel): boolean {
      return notification.isRead;
    },
    isInitiallyUnread(notification: Notification | NotificationFromNolaNovel): boolean {
      const key = this.getNotificationKey(notification);
      return this.initialUnreadIds.has(key);
    },
    formatDate(notification: Notification | NotificationFromNolaNovel): string {
      let date: Date;
      if (this.notificationType === "agent") {
        date = new Date((notification as Notification).createdAt);
      } else {
        date = new Date((notification as NotificationFromNolaNovel).publishedAt);
      }
      return format(date, this.$t("inboxPage.dateFormat") as string);
    },
    getCategoryLabel(notification: Notification | NotificationFromNolaNovel): string {
      if (this.notificationType === "agent") {
        // エージェント通知のカテゴリ
        return this.$t("inboxPage.agentSubTabs.agent") as string;
      }
      // Nolaノベル通知のカテゴリ
      const nolaNotif = notification as NotificationFromNolaNovel;
      if (this.isReaderNotification(nolaNotif)) {
        return this.$t("nolaNovelNotificationPage.tabs.fromReaders") as string;
      }
      if (this.isEditorNotification(nolaNotif)) {
        return this.$t("nolaNovelNotificationPage.tabs.fromEditors") as string;
      }
      return this.$t("nolaNovelNotificationPage.tabs.others") as string;
    },
    getTitle(notification: Notification | NotificationFromNolaNovel): string {
      if (this.notificationType === "agent") {
        // エージェント通知のタイトル（HTMLタグを除去）
        const agentNotif = notification as Notification;
        const div = document.createElement("div");
        div.innerHTML = agentNotif.message;
        const text = div.textContent || div.innerText || "";
        // 最初の行または最初の50文字をタイトルとして使用
        const firstLine = text.split("\n")[0];
        return firstLine.length > 50 ? `${firstLine.substring(0, 50)}...` : firstLine;
      }
      // Nolaノベル通知のタイトル
      return this.getNolaNovelTitle(notification as NotificationFromNolaNovel);
    },
    getNolaNovelTitle(notification: NotificationFromNolaNovel): string {
      const { type, novel, user, episode, company } = notification;

      const fallbackNovel = this.$t("nolaNovelNotificationListItem.fallback.novel") as string;
      const fallbackUser = this.$t("nolaNovelNotificationListItem.fallback.user") as string;
      const fallbackCompany = this.$t("nolaNovelNotificationListItem.fallback.company") as string;

      switch (type) {
        case NotificationDataType.NOVEL_UPDATED:
          return this.$t("nolaNovelNotificationListItem.novelUpdated", { title: novel?.title || fallbackNovel }) as string;
        case NotificationDataType.FOLLOW_NEWNOVEL:
          return this.$t("nolaNovelNotificationListItem.followNewNovel", { name: user?.name || fallbackUser }) as string;
        case NotificationDataType.FOLLOW_NEWEPISODE:
          return this.$t("nolaNovelNotificationListItem.followNewEpisode", { name: user?.name || fallbackUser }) as string;
        case NotificationDataType.BOOKMARK:
          return this.$t("nolaNovelNotificationListItem.bookmark", { title: novel?.title || fallbackNovel }) as string;
        case NotificationDataType.REVIEW:
          return this.$t("nolaNovelNotificationListItem.review", { title: novel?.title || fallbackNovel }) as string;
        case NotificationDataType.FOLLOW:
          return this.$t("nolaNovelNotificationListItem.follow", { name: user?.name || fallbackUser }) as string;
        case NotificationDataType.COMMENT:
          return this.$t("nolaNovelNotificationListItem.comment", { title: episode?.title || "" }) as string;
        case NotificationDataType.COMMENT_REPLY:
          return this.$t("nolaNovelNotificationListItem.commentReply", { title: episode?.title || "" }) as string;
        case NotificationDataType.FOLLOW_NEWACTIVITY:
          return this.$t("nolaNovelNotificationListItem.followNewActivity", { name: user?.name || fallbackUser }) as string;
        case NotificationDataType.COMMENT_ACTIVITY:
          return this.$t("nolaNovelNotificationListItem.commentActivity") as string;
        case NotificationDataType.COMMENT_ACTIVITY_REPLY:
          return this.$t("nolaNovelNotificationListItem.commentActivityReply") as string;
        case NotificationDataType.FAVORITE_ACTIVITY:
          return this.$t("nolaNovelNotificationListItem.favoriteActivity") as string;
        case NotificationDataType.COMPANY_FOOTPRINTS:
          return this.$t("nolaNovelNotificationListItem.companyFootprints", {
            title: novel?.title || fallbackNovel,
            company: company?.name || fallbackCompany,
          }) as string;
        case NotificationDataType.COMPANY_FOOTPRINTS_EPISODE:
          return this.$t("nolaNovelNotificationListItem.companyFootprintsEpisode", {
            title: novel?.title || fallbackNovel,
            episodeTitle: episode?.title || "",
            company: company?.name || fallbackCompany,
          }) as string;
        case NotificationDataType.COMPANY_BOOKMARK:
          return this.$t("nolaNovelNotificationListItem.companyBookmark", {
            title: novel?.title || fallbackNovel,
            company: company?.name || fallbackCompany,
          }) as string;
        case NotificationDataType.COMPANY_FEATURED_NOVEL:
          return this.$t("nolaNovelNotificationListItem.companyFeaturedNovel", {
            title: novel?.title || fallbackNovel,
            company: company?.name || fallbackCompany,
          }) as string;
        case NotificationDataType.ADMIN_PICKUP:
          return this.$t("nolaNovelNotificationListItem.adminPickup", { title: novel?.title || fallbackNovel }) as string;
        default:
          return this.$t("nolaNovelNotificationListItem.defaultNotification") as string;
      }
    },
    getNolaNovelContent(notification: Notification | NotificationFromNolaNovel): string {
      // 詳細コンテンツ（展開時に表示）
      return this.getNolaNovelTitle(notification as NotificationFromNolaNovel);
    },
    getAgentMessage(notification: Notification | NotificationFromNolaNovel): string {
      return (notification as Notification).message || "";
    },
    handleNolaNovelClick(notification: Notification | NotificationFromNolaNovel) {
      this.onNolaNovelClick(notification as NotificationFromNolaNovel);
    },
    isReaderNotification(notification: NotificationFromNolaNovel): boolean {
      return (
        notification.type === NotificationDataType.BOOKMARK ||
        notification.type === NotificationDataType.FOLLOW ||
        notification.type === NotificationDataType.REVIEW ||
        notification.type === NotificationDataType.COMMENT ||
        notification.type === NotificationDataType.COMMENT_REPLY ||
        notification.type === NotificationDataType.COMMENT_ACTIVITY ||
        notification.type === NotificationDataType.COMMENT_ACTIVITY_REPLY ||
        notification.type === NotificationDataType.FAVORITE_ACTIVITY
      );
    },
    isEditorNotification(notification: NotificationFromNolaNovel): boolean {
      return (
        notification.type === NotificationDataType.COMPANY_FOOTPRINTS ||
        notification.type === NotificationDataType.COMPANY_FOOTPRINTS_EPISODE ||
        notification.type === NotificationDataType.COMPANY_FEATURED_NOVEL ||
        notification.type === NotificationDataType.COMPANY_BOOKMARK ||
        notification.type === NotificationDataType.ADMIN_PICKUP
      );
    },
    getNolaNovelPath(notification: NotificationFromNolaNovel): string {
      const { type, novel, user, episode, comment, activity, company } = notification;

      switch (type) {
        case NotificationDataType.NOVEL_UPDATED:
        case NotificationDataType.FOLLOW_NEWNOVEL:
        case NotificationDataType.FOLLOW_NEWEPISODE:
        case NotificationDataType.BOOKMARK:
        case NotificationDataType.REVIEW:
          return `/novel/${novel?.id || ""}`;
        case NotificationDataType.FOLLOW:
          return `/user/${user?.id || ""}`;
        case NotificationDataType.COMMENT:
        case NotificationDataType.COMMENT_REPLY:
          return `/comments/${episode?.id || ""}?id=${comment?.id || ""}`;
        case NotificationDataType.FAVORITE_ACTIVITY:
          return `/activity/${activity?.id || ""}`;
        case NotificationDataType.COMMENT_ACTIVITY:
        case NotificationDataType.COMMENT_ACTIVITY_REPLY:
          return `/activity/${activity?.id || ""}?id=${comment?.id || ""}`;
        case NotificationDataType.FOLLOW_NEWACTIVITY:
          return `/activity/${activity?.id || ""}`;
        case NotificationDataType.COMPANY_BOOKMARK:
        case NotificationDataType.COMPANY_FOOTPRINTS:
        case NotificationDataType.COMPANY_FOOTPRINTS_EPISODE:
          return `/company/${company?.id || ""}`;
        case NotificationDataType.COMPANY_FEATURED_NOVEL:
          return `/?featuredCompany=${company?.id || ""}`;
        case NotificationDataType.ADMIN_PICKUP:
          return "/?id=adminPickup";
        default:
          return "/notification";
      }
    },
    onNolaNovelClick(notification: NotificationFromNolaNovel) {
      const path = this.getNolaNovelPath(notification);
      window.open(
        createUrlWithUtmParams(
          `${process.env.VUE_APP_NOLANOVEL_WEB}${path}`,
          NolaPageName.NolaNovelNotification,
          NolaItemId.NotificationItem
        ),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
      if (!notification.isRead) {
        this.$store.dispatch("nolaNovelModule/readNotification", notification.typeTargetIdSubTargetId);
      }
    },
  },
});

interface Props {
  notifications: Notification[] | NotificationFromNolaNovel[];
  notificationType: NotificationType;
  isLoadComplete: boolean;
}

interface Data {
  initialUnreadIds: Set<string>;
}

interface Computed {}

interface Methods {
  getNotificationKey(notification: Notification | NotificationFromNolaNovel): string;
  isRead(notification: Notification | NotificationFromNolaNovel): boolean;
  isInitiallyUnread(notification: Notification | NotificationFromNolaNovel): boolean;
  formatDate(notification: Notification | NotificationFromNolaNovel): string;
  getCategoryLabel(notification: Notification | NotificationFromNolaNovel): string;
  getTitle(notification: Notification | NotificationFromNolaNovel): string;
  getNolaNovelTitle(notification: NotificationFromNolaNovel): string;
  getNolaNovelContent(notification: Notification | NotificationFromNolaNovel): string;
  getAgentMessage(notification: Notification | NotificationFromNolaNovel): string;
  handleNolaNovelClick(notification: Notification | NotificationFromNolaNovel): void;
  isReaderNotification(notification: NotificationFromNolaNovel): boolean;
  isEditorNotification(notification: NotificationFromNolaNovel): boolean;
  getNolaNovelPath(notification: NotificationFromNolaNovel): string;
  onNolaNovelClick(notification: NotificationFromNolaNovel): void;
}
